import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useLocation } from 'react-router';

export type EcommerceGtmDataLayerFieldsType = {
  transaction_id: number; // The unique identifier of a transaction.
  value: number; // The monetary value of the event.
  currency: string; // Currency of the items associated with the event, in 3-letter ISO 4217 format.
};

export const useAddGtm = () => {
  const executeAdditionalGTMScript = (userId: number | undefined) => {
    (function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'userId',
        user_id: userId ? userId.toString() : userId
      });
    })();
  };

  const executeRegistrationGTMScript = (userId: number) => {
    executeAdditionalGTMScript(userId);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'registration',
      user_id: userId.toString()
    });
  };

  const executeLoginGTMScript = (userId: number) => {
    executeAdditionalGTMScript(userId);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'login',
      user_id: userId.toString()
    });
  };

  const generateAnyGtmScriptEvent =
    (event: string) => (args: { [key: string]: string | unknown }) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event,
        ...args
      });
    };

  const generateFailureGtmScript = (event: string) => (message: string) =>
    generateAnyGtmScriptEvent(event)({ error_desc: message });

  const pushGameLaunchFailureToGtmDataLayer = generateFailureGtmScript('Game_Launch_Failure');

  const pushRegistrationFailureToGtmDataLayer = generateFailureGtmScript('Registration_Failure');

  const pushLoginFailureToGtmDataLayer = generateFailureGtmScript('Login_Failure');

  const pushGameLaunchSuccessToGtmDataLayer = () =>
    generateAnyGtmScriptEvent('Game_Launch_Success')({});

  const pushPurchaseSuccessToGtmDataLayer = (data: EcommerceGtmDataLayerFieldsType) =>
    generateAnyGtmScriptEvent('purchase')({
      ecommerce: { ...data, items: [{ item_name: 'deposit' }] }
    });

  const pushAddPaymentInfoToGtmDataLayer = (data: EcommerceGtmDataLayerFieldsType) =>
    generateAnyGtmScriptEvent('add_payment_info')({
      ecommerce: { ...data, items: [{ item_name: 'deposit' }] }
    });

  const pushRefundToGtmDataLayer = (data: EcommerceGtmDataLayerFieldsType) =>
    generateAnyGtmScriptEvent('refund')({
      ecommerce: { ...data, items: [{ item_name: 'deposit' }] }
    });

  return {
    pushRefundToGtmDataLayer,
    pushAddPaymentInfoToGtmDataLayer,
    pushPurchaseSuccessToGtmDataLayer,
    pushGameLaunchSuccessToGtmDataLayer,
    pushLoginFailureToGtmDataLayer,
    pushRegistrationFailureToGtmDataLayer,
    pushGameLaunchFailureToGtmDataLayer,
    executeLoginGTMScript,
    executeRegistrationGTMScript,
    executeAdditionalGTMScript
  };
};

export const useAddGtmEffect = () => {
  const location = useLocation();
  const { executeAdditionalGTMScript } = useAddGtm();
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    executeAdditionalGTMScript(userId ? parseInt(userId) : undefined);
  }, [location.pathname]);
};

export const initializeGtm = ({
  isProductionEnv,
  gtmId
}: {
  isProductionEnv: boolean;
  gtmId: string;
}) => {
  const executeAdditionalGTMScript = (userId: number | undefined) => {
    (function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'userId',
        user_id: userId ? userId.toString() : userId
      });
    })();
  };
  if (isProductionEnv) {
    const userId = localStorage.getItem('userId');
    const tagManagerArgs = {
      gtmId,
      events: {
        user_id: userId || undefined
      }
    };
    executeAdditionalGTMScript(userId ? parseInt(userId) : undefined);
    TagManager.initialize(tagManagerArgs);
  }
};

/**
 * Хук для GTM(Google Tag Manager).
 *
 * @returns 10 функций.
 * -pushRefundToGtmDataLayer ее использовать не надо, она уже используется на onSucces в хуке useMakeWithdrawal
 * -pushPurchaseSuccessToGtmDataLayer ее использовать не надо, она уже используется на onSucces в хуке useMakeDeposit
 * -pushGameLaunchSuccessToGtmDataLayer ее использовать не надо, она уже используется на onSucces в хуке useGetGame
 * -pushGameLaunchFailureToGtmDataLayer ее использовать не надо, она уже используется на onError в хуке useGetGame
 * -pushLoginFailureToGtmDataLayer ее использовать не надо, она уже используется на onError в хуке useLogin
 * -executeLoginGTMScript ее использовать не надо, она уже используется на onError в хуке useLogin
 * -pushRegistrationFailureToGtmDataLayer ее использовать не надо, она уже используется на onError в хуке useRegistration
 * -executeRegistrationGTMScript ее использовать не надо, она уже используется на onSuccess в хуке useRegistration
 * -executeAdditionalGTMScript ее использовать не надо, она уже используется на onSuccess в хуке useGetFullInfo
 * -pushAddPaymentInfoToGtmDataLayer пока не знаем где ее использовать, но как только узнаем, напишем
 *
 * @returns useAddGtmEffect это надо вставить в Router, но для того чтобы все работало корректно нужно чтобы useAddGtmEffect был внутри BrowserRouter
 * @returns initializeGtm служит для инициализации GTM нужно вызвать НЕ внутри компонентов, а за его пределами.
 *  @param isProductionEnv для определения prod или нет
 *  @param gtmId id для GTM должны скинуть вместе со скриптом
 */
